/**
 * Meeting Album Page
 * /meetings/:id/album - 모임 앨범 페이지
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { normalizeImageUrl } from "@/lib/image-url";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";

interface Meeting {
  id: string;
  title: string;
  photos: string[];
}

export default function MeetingAlbumPage() {
  const [match, params] = useRoute<{ id: string }>("/meetings/:id/album");
  const [, navigate] = useLocation();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );

  const meetingId = params?.id;

  // 모임 상세 조회
  const { data: meetingData, isLoading } = useQuery<{
    ok: boolean;
    data: Meeting;
  }>({
    queryKey: ["/api/meetings", meetingId],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}`);
      if (!response.ok) throw new Error("Failed to fetch meeting");
      return response.json();
    },
    enabled: !!meetingId,
  });

  const meeting = meetingData?.ok ? meetingData.data : null;

  // 게시글 이미지도 포함하여 앨범 구성 (MeetingDetail.tsx와 동일한 로직)
  const rawPhotos = Array.isArray(meeting?.photos) ? meeting.photos : [];
  const posts = Array.isArray((meeting as any)?.posts)
    ? (meeting as any).posts
    : [];

  const photos = (() => {
    const seen = new Set<string>();
    const addPhoto = (src?: string | null) => {
      if (!src) return;
      const normalized = normalizeImageUrl(src) || src;
      if (normalized) {
        seen.add(normalized);
      }
    };
    rawPhotos.forEach(addPhoto);
    posts.forEach((post: any) => {
      if (Array.isArray(post.images)) {
        post.images.forEach(addPhoto);
      }
    });
    return Array.from(seen);
  })();

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              유효하지 않은 모임 ID입니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">로딩 중...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              모임 정보를 찾을 수 없습니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/meetings/${meetingId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">
            {meeting.title} - 앨범 ({photos.length}장)
          </h1>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div
                key={index}
                className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={photo}
                  alt={`${meeting.title} 사진 ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">아직 사진이 없습니다</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* 이미지 모달 */}
      {selectedImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImageIndex(null)}
        >
          <div className="relative max-w-7xl max-h-full">
            <img
              src={photos[selectedImageIndex]}
              alt={`${meeting.title} 사진 ${selectedImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20"
              onClick={() => setSelectedImageIndex(null)}
            >
              ✕
            </Button>
            {selectedImageIndex > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(selectedImageIndex - 1);
                }}
              >
                ←
              </Button>
            )}
            {selectedImageIndex < photos.length - 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(selectedImageIndex + 1);
                }}
              >
                →
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
